import { CommonModule } from '@angular/common';
import { Component, Input, forwardRef, OnInit, ElementRef, ViewChild, HostListener, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DropdownOption } from 'src/app/models/shared/dropdown-option';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-dropdown',
  templateUrl: './dropdown.component.html',
  styleUrls: ['./dropdown.component.css'],
  imports: [FormsModule, ReactiveFormsModule, CommonModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DropdownComponent),
      multi: true
    }
  ],

})
export class DropdownComponent implements ControlValueAccessor, OnInit, OnDestroy, OnChanges {
  @Input() options: DropdownOption[] = [];
  @Input() placeholder = 'Seleccionar';
  @Input() label = '';
  @Input() searchPlaceholder = 'Buscar';
  @Input() multiple = false; // Nueva propiedad para habilitar selección múltiple
  @Input() showSearch = true;
  @Input() maxDisplayItems = 2; // Número máximo de elementos a mostrar en el botón
  @Input() formControl?: FormControl; // Support for reactive forms
  @ViewChild('dropdownContainer') dropdownContainer!: ElementRef;

  isOpen = false;
  searchText = '';
  selectedOptions: DropdownOption[] = []; // Ahora es un array de opciones
  filteredOptions: DropdownOption[] = [];
  disabled = false;

  // For cleanup of subscriptions
  private destroy$ = new Subject<void>();

  // Control Value Accessor methods
  onChange: any = () => {};
  onTouched: any = () => {};

  private internalValue: any;

  constructor() { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['options'] && changes['options'].currentValue?.length > 0) {
      this.writeValue(this.internalValue);
    }
  }

  ngOnInit(): void {
    this.filteredOptions = [...this.options];
    
    // If a FormControl is provided directly, subscribe to its value changes
    if (this.formControl) {
      // Update initial value
      this.writeValue(this.formControl.value);
      
      // Subscribe to value changes
      this.formControl.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(value => {
          this.writeValue(value);
        });
    }
  }
  
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleDropdown(): void {
    if (!this.disabled) {
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        this.filteredOptions = [...this.options];
        this.searchText = '';
        setTimeout(() => {
          const searchInput = document.querySelector('.dropdown-search input') as HTMLInputElement;
          if (searchInput) {
            searchInput.focus();
          }
        }, 0);
      }
    }
  }

  isSelected(option: DropdownOption): boolean {
    return this.selectedOptions.some(item => item.id === option.id);
  }

  toggleOption(option: DropdownOption, event?: MouseEvent): void {
    if (event) {
      event.stopPropagation(); // Evitar que se cierre el dropdown
    }

    if (this.multiple) {
      const index = this.selectedOptions.findIndex(item => item.id === option.id);
      
      if (index === -1) {
        // Añadir la opción si no está seleccionada
        this.selectedOptions = [...this.selectedOptions, option];
      } else {
        // Eliminar la opción si ya está seleccionada
        this.selectedOptions = this.selectedOptions.filter(item => item.id !== option.id);
      }
      
      // Emitir el array de IDs seleccionados
      const value = this.selectedOptions.map(item => item.id);
      this.onChange(value);
      
      // If using direct FormControl, update it
      if (this.formControl && !this.formControl.disabled) {
        this.formControl.setValue(value, { emitEvent: false });
      }
    } else {
      // Comportamiento original para selección única
      this.selectedOptions = [option];
      const value = option.id;
      this.onChange(value);
      
      // If using direct FormControl, update it
      if (this.formControl && !this.formControl.disabled) {
        this.formControl.setValue(value, { emitEvent: false });
      }
      
      this.isOpen = false;
    }
    
    this.onTouched();
  }

  filterOptions(): void {
    if (!this.searchText.trim()) {
      this.filteredOptions = [...this.options];
    } else {
      const searchLower = this.searchText.toLowerCase();
      this.filteredOptions = this.options.filter(option => 
        option.text.toLowerCase().includes(searchLower) || 
        (option.code && option.code.toLowerCase().includes(searchLower))
      );
    }
  }

  // Método para mostrar el texto seleccionado en el botón
  getSelectedText(): string {
    if (this.selectedOptions.length === 0) {
      return this.placeholder;
    }

    if (!this.multiple) {
      return this.selectedOptions[0].text;
    }

    // Para selección múltiple
    if (this.selectedOptions.length <= this.maxDisplayItems) {
      return this.selectedOptions.map(option => option.text).join(', ');
    } else {
      return `${this.selectedOptions.length} elementos seleccionados`;
    }
  }

  // Método para limpiar todas las selecciones
  clearSelections(event: MouseEvent): void {
    event.stopPropagation();
    this.selectedOptions = [];
    const value = this.multiple ? [] : null;
    this.onChange(value);
    
    // If using direct FormControl, update it
    if (this.formControl && !this.formControl.disabled) {
      this.formControl.setValue(value, { emitEvent: false });
    }
    
    this.onTouched();
  }

  // ControlValueAccessor implementation
  writeValue(value: any): void {
    this.internalValue = value;
    if (value === null || value === undefined) {
      this.selectedOptions = [];
      return;
    }

    if (this.multiple && Array.isArray(value)) {
      // Para selección múltiple, esperamos un array de IDs
      this.selectedOptions = this.options.filter(option => 
        value.includes(option.id)
      );
    } else {
      // Para selección única
      const option = this.options.find(opt => opt.id === value);
      this.selectedOptions = option ? [option] : [];
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event): void {
    if (this.isOpen && !this.dropdownContainer.nativeElement.contains(event.target)) {
      this.isOpen = false;
    }
  }
}